// The optimistic-lifecycle status of a single zone's controls, derived from
// darwin_core's pending slices. This is the one place the web maps darwin_core's
// pending-request bookkeeping onto the pending / confirmed vocabulary the
// controls render, so anything showing the state of the commanded VALUE reads
// the same signal rather than re-deriving it.
//
// darwin_core tracks an in-flight control command as a request in one of the
// per-zone pending slices (pendingMode / pendingSetpoint / pendingHolidayMode),
// keyed by homeId + [prtId, endpointId]. Its lifecycle (see pending-reducer.js):
//   - *_STARTED       → a request is added with a real requestId  → "pending"
//   - *_RECEIVED (ack)→ that request's requestId is set to 'done' → "confirmed"
//   - CLEAR_PENDING_STATE (saga, ~7s after ack+matching live) removes it → idle
//   - *_REJECTED / PENDING_CHANGE_TIMED_OUT → request removed, error pushed → idle
//     (the error surfaces separately, via ControlErrorBanner)
//
// The *_RECEIVED ack here is the device's legacy completion frame (`setPrtMode…`
// with state 'complete'). On a device-service home nothing in the desired-state
// flow sends one, so a request sits "pending" until its timeout. That makes this
// status the wrong signal for "is a command still on its way" — the header
// spinner reads the transport's own in-flight trackers instead (darwin_core's
// isCommandInFlight). This status stays the source for the value's
// pending/confirmed badge only.
//
// getPendingData is imported from darwin_core rather than re-implemented so the
// home/zone matching can never drift from the reducer that writes these slices.
import { getPendingData } from 'darwin_core/reducers/pending-reducer.js';

export const CONTROL_STATUS = {
  Idle: 'idle',
  Pending: 'pending',
  Confirmed: 'confirmed',
};

// Returns 'pending' if any control command for this zone is still awaiting its
// ack, 'confirmed' in the brief window after an ack lands but before the pending
// state is cleared, otherwise 'idle'. A live pending request (requestId not yet
// 'done') always wins over a confirmed one, so a zone with a fresh command still
// in flight reads as pending even if an earlier command is sitting confirmed.
export function zoneControlStatus(state, homeId, zoneId) {
  const slices = [
    state.pendingMode,
    state.pendingSetpoint,
    state.pendingHolidayMode,
  ];

  let sawConfirmed = false;

  for (const slice of slices) {
    const pending = getPendingData(slice, homeId, zoneId);
    if (!pending) {
      continue;
    }
    if (pending.requestId === 'done') {
      sawConfirmed = true;
    } else {
      return CONTROL_STATUS.Pending;
    }
  }

  return sawConfirmed ? CONTROL_STATUS.Confirmed : CONTROL_STATUS.Idle;
}
